import React, { useMemo, useState } from "react";

import { SxProps, Box, Collapse, SvgIcon } from "@mui/material";
import { StriimTypography } from "@striim/striim-ui-v2";

import ComponentDnD from "./ComponentDnD";
import { LeftSidebarSubcategory } from "../leftSidebar.types";
import { FilterBoolean, Styles, StylesTheme } from "../../../../../../src/utils/types";
import { ChevronUp } from "../../../../../../src/generic/icon/customIcons";
import { getCustomDisableReason } from "./CategoryContent.utils";

interface ExpandSubcategoryProps {
    subcategory: LeftSidebarSubcategory;
    scrollableRef?: React.RefObject<HTMLDivElement>;
    initialInView?: boolean;
}

const ExpandSubcategory: React.FC<ExpandSubcategoryProps> = ({ subcategory, scrollableRef, initialInView }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const chevronSX: SxProps = useMemo(
        () => [styles.chevron, !isExpanded && styles.chevronCollapsed].filter(FilterBoolean),
        [isExpanded]
    );

    return (
        <div data-testid="subcategory" data-testkey={subcategory.name}>
            <Box
                onClick={() => setIsExpanded(prevState => !prevState)}
                sx={styles.subcategoryWrap}
                data-testid={`${isExpanded ? "toggle" : "expand"}-subcategory`}
            >
                <SvgIcon component={ChevronUp} sx={chevronSX} />

                <StriimTypography
                    variant="h4"
                    color="greyscale.900"
                    data-testid="subcategory-name"
                    sx={styles.title}
                    title={subcategory.name}
                >
                    {subcategory.name}
                </StriimTypography>
            </Box>

            <Collapse in={isExpanded} timeout={transitionTime} sx={styles.collapse}>
                {subcategory.components.map((component, i) => (
                    <ComponentDnD
                        key={component.name}
                        id={component.id}
                        name={component.name}
                        disabled={component.isEnabled === false}
                        customDisableReason={getCustomDisableReason(component)}
                        type={component.type}
                        scrollableRef={scrollableRef}
                        initialInView={initialInView && i < 15}
                    />
                ))}
            </Collapse>
        </div>
    );
};

export default ExpandSubcategory;

const transitionTime = 200;

const styles: Styles = {
    subcategoryWrap: {
        display: "flex",
        px: 2,
        py: 1,

        cursor: "pointer",
        "&:hover, &:focus": {
            backgroundColor: ({ palette }: StylesTheme) => palette.greyscale[100]
        },
        "&:active": {
            backgroundColor: ({ palette }: StylesTheme) => palette.greyscale[200]
        }
    },
    chevron: {
        width: 18,
        height: 18,
        margin: "auto 0",
        color: "transparent",
        transition: `transform ${transitionTime}ms`
    },
    chevronCollapsed: {
        transform: "rotate(180deg)"
    },
    title: {
        pl: 2,
        lineHeight: "22px !important",

        userSelect: "none",

        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis"
    },
    collapse: {
        "& .MuiCollapse-wrapperInner": {
            display: "flex",
            flexDirection: "column"
        }
    }
};
